import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RepairCostsLibrary.css';
import { Input, Loading } from '.';
import { REHAB_LEVELS_TEMPLATE } from '../defaults';

const PREVIEW_SQFTS = [1500, 2500, 4000, 6000];
const AUTOSAVE_DEBOUNCE_MS = 800;
const SAVED_FLASH_MS = 2000;
const RETRY_BACKOFF_MS = [2000, 5000, 15000]; // 2s → 5s → 15s, then stops auto-retrying

// Field paths written by autosave. Using `mergeFields` (instead of `merge: true`) ensures any
// other fields under `template3` — most importantly `big_ticket_items` — are preserved if a
// future code path or admin tool ever writes them.
const MERGE_FIELDS = [
    'activeTemplate',
    'template3.levels',
    'template3.scalingBase',
    'template3.scalingInterval',
    'template3.scalingBaselineSqft',
    'template3.minScalingFactor',
];

const buildDefaultTemplate = () => ({
    type: 3,
    levels: REHAB_LEVELS_TEMPLATE.levels.map(l => ({ ...l })),
    scalingBase: REHAB_LEVELS_TEMPLATE.scalingBase,
    scalingInterval: REHAB_LEVELS_TEMPLATE.scalingInterval,
    scalingBaselineSqft: REHAB_LEVELS_TEMPLATE.scalingBaselineSqft,
    minScalingFactor: REHAB_LEVELS_TEMPLATE.minScalingFactor,
});

const normalize = (tmpl) => {
    const defaults = buildDefaultTemplate();
    if (!tmpl) return defaults;
    const levelsByLevel = new Map();
    (tmpl.levels || []).forEach(l => {
        if (l && typeof l.level === 'number') levelsByLevel.set(l.level, l);
    });
    const mergedLevels = defaults.levels.map(d => {
        const stored = levelsByLevel.get(d.level);
        return {
            ...d,
            ...(stored || {}),
            pricePerSqft: typeof stored?.pricePerSqft === 'number' ? stored.pricePerSqft : d.pricePerSqft,
            name: stored?.name || d.name,
            description: stored?.description || d.description,
            timeline: stored?.timeline || d.timeline,
        };
    });
    return {
        type: 3,
        levels: mergedLevels,
        scalingBase: typeof tmpl.scalingBase === 'number' ? tmpl.scalingBase : defaults.scalingBase,
        scalingInterval: typeof tmpl.scalingInterval === 'number' ? tmpl.scalingInterval : defaults.scalingInterval,
        scalingBaselineSqft: typeof tmpl.scalingBaselineSqft === 'number' ? tmpl.scalingBaselineSqft : defaults.scalingBaselineSqft,
        minScalingFactor: typeof tmpl.minScalingFactor === 'number' ? tmpl.minScalingFactor : defaults.minScalingFactor,
    };
};

export const computeCost = (pricePerSqft, sqft, tmpl) => {
    const safeInterval = Math.max(1, tmpl.scalingInterval);
    const steps = Math.max(0, Math.floor((sqft - tmpl.scalingBaselineSqft) / safeInterval));
    const factor = Math.max(Math.pow(tmpl.scalingBase, steps), tmpl.minScalingFactor);
    return Math.round((Math.max(0, pricePerSqft) * factor * sqft) / 1000) * 1000;
};

const formatCurrency = (n) => `$${Math.round(n).toLocaleString()}`;

const sameTemplate = (a, b) => {
    if (a.scalingBase !== b.scalingBase) return false;
    if (a.scalingInterval !== b.scalingInterval) return false;
    if (a.scalingBaselineSqft !== b.scalingBaselineSqft) return false;
    if (a.minScalingFactor !== b.minScalingFactor) return false;
    if (a.levels.length !== b.levels.length) return false;
    for (let i = 0; i < a.levels.length; i++) {
        if (a.levels[i].pricePerSqft !== b.levels[i].pricePerSqft) return false;
    }
    return true;
};

const SCALING_FIELD_CLAMPS = {
    scalingBaselineSqft: (n) => Math.max(0, n),
};

const sanitizeForSave = (tmpl) => ({
    ...tmpl,
    scalingBaselineSqft: Math.max(0, tmpl.scalingBaselineSqft),
    scalingInterval: Math.max(1, tmpl.scalingInterval),
    scalingBase: Math.max(0, Math.min(1, tmpl.scalingBase)),
    minScalingFactor: Math.max(0, Math.min(1, tmpl.minScalingFactor)),
    levels: tmpl.levels.map(l => ({ ...l, pricePerSqft: Math.max(0, l.pricePerSqft) })),
});

const buildPayload = (snapshot) => {
    const levelsToSave = snapshot.levels.map((l, i) => ({
        id: l.id || `level_${l.level ?? i}`,
        level: typeof l.level === 'number' ? l.level : i,
        pricePerSqft: l.pricePerSqft,
        ...(l.name && { name: l.name }),
        ...(l.description && { description: l.description }),
        ...(l.timeline && { timeline: l.timeline }),
    }));
    return {
        activeTemplate: 3,
        template3: {
            levels: levelsToSave,
            scalingBase: snapshot.scalingBase,
            scalingInterval: snapshot.scalingInterval,
            scalingBaselineSqft: snapshot.scalingBaselineSqft,
            minScalingFactor: snapshot.minScalingFactor,
        },
    };
};

function RepairCostsLibrary({ companyId, repairTemplate, setRepairTemplate }) {
    const [edits, setEdits] = useState(() => normalize(repairTemplate));
    // status: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
    const [status, setStatus] = useState('idle');
    const [isInitialized, setIsInitialized] = useState(() =>
        !!(repairTemplate && Array.isArray(repairTemplate.levels))
    );
    // Per-input raw string overrides. Lets users type "1." or clear a field mid-edit without
    // the controlled-input pattern re-stringifying the parsed number on every keystroke.
    // Cleared on blur, so display reverts to the canonical String(value).
    const [rawInputs, setRawInputs] = useState({});

    const baselineRef = useRef(normalize(repairTemplate));
    const editsRef = useRef(edits);
    const debounceTimer = useRef(null);
    const flashTimer = useRef(null);
    const retryTimer = useRef(null);
    const retryAttempt = useRef(0);
    const pendingSnapshot = useRef(null); // Set when a debounce is scheduled but hasn't fired
    const inFlight = useRef(false);
    const isMounted = useRef(true);

    useEffect(() => { editsRef.current = edits; }, [edits]);

    const setStatusIfMounted = useCallback((s) => {
        if (isMounted.current) setStatus(s);
    }, []);

    const performSave = useCallback(async (snapshotToSave) => {
        const { getFirestore, doc, setDoc } = await import('firebase/firestore');
        const db = getFirestore();
        const docRef = doc(db, 'repair_template', companyId);
        await setDoc(docRef, buildPayload(snapshotToSave), { mergeFields: MERGE_FIELDS });
    }, [companyId]);

    const runSave = useCallback(async (snapshotToSave) => {
        inFlight.current = true;
        setStatusIfMounted('saving');
        try {
            await performSave(snapshotToSave);
            baselineRef.current = snapshotToSave;
            // Only clear if the pending snapshot is the one we just saved. If a newer edit
            // arrived during the save, leave pendingSnapshot intact so an unmount before the
            // next debounced fire still flushes it.
            if (pendingSnapshot.current && sameTemplate(pendingSnapshot.current, snapshotToSave)) {
                pendingSnapshot.current = null;
            }
            retryAttempt.current = 0;
            if (setRepairTemplate && isMounted.current) setRepairTemplate(snapshotToSave);
            setStatusIfMounted('saved');
            if (flashTimer.current) clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setStatusIfMounted('idle'), SAVED_FLASH_MS);
        } catch (err) {
            console.error('Failed to autosave repair template:', err);
            setStatusIfMounted('error');
            // Auto-retry with backoff. The retry uses the LATEST edits, not the failed snapshot,
            // so any keystrokes the user made since the failure are included.
            const delay = RETRY_BACKOFF_MS[Math.min(retryAttempt.current, RETRY_BACKOFF_MS.length - 1)];
            retryAttempt.current += 1;
            if (retryAttempt.current <= RETRY_BACKOFF_MS.length) {
                if (retryTimer.current) clearTimeout(retryTimer.current);
                retryTimer.current = setTimeout(() => {
                    if (!isMounted.current) return;
                    const latest = sanitizeForSave(editsRef.current);
                    if (!sameTemplate(latest, baselineRef.current)) {
                        runSave(latest);
                    }
                }, delay);
            }
        } finally {
            inFlight.current = false;
        }
    }, [performSave, setRepairTemplate, setStatusIfMounted]);

    // One-time initialize from parent's prop. Subsequent prop changes are handled by onSnapshot,
    // and our own saves (which propagate via setRepairTemplate) won't re-clobber edits because
    // `isInitialized` is sticky.
    useEffect(() => {
        if (isInitialized) return;
        if (!repairTemplate || !Array.isArray(repairTemplate.levels)) return;
        const initial = normalize(repairTemplate);
        baselineRef.current = initial;
        setEdits(initial);
        setIsInitialized(true);
    }, [repairTemplate, isInitialized]);

    // Real-time subscription. Initial onSnapshot delivery also acts as the loader if the parent
    // hasn't populated the prop yet. Subsequent remote changes only sync if the local view is
    // clean — never disturb in-progress local edits.
    useEffect(() => {
        if (!companyId) return undefined;
        let unsub = null;
        let cancelled = false;
        (async () => {
            const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
            if (cancelled) return;
            const db = getFirestore();
            unsub = onSnapshot(doc(db, 'repair_template', companyId), (snap) => {
                if (!isMounted.current) return;
                const data = snap.exists() ? snap.data() : null;
                const incoming = normalize(data?.template3 || data);
                // First delivery is the loader: clear the spinner even when `incoming` matches
                // our default baseline (a company with no saved template3). Must happen before the
                // equality guards below, or those companies stay stuck on <Loading /> forever.
                if (!isInitialized) setIsInitialized(true);
                // Don't disturb a save that's currently in flight or an unsaved local edit.
                if (inFlight.current) return;
                const localDirty = !sameTemplate(editsRef.current, baselineRef.current);
                if (localDirty) return;
                if (sameTemplate(incoming, baselineRef.current)) return;
                baselineRef.current = incoming;
                setEdits(incoming);
            });
        })();
        return () => {
            cancelled = true;
            if (unsub) unsub();
        };
    // We intentionally don't list isInitialized — we only want this to re-subscribe on companyId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    // Autosave loop: debounce, snapshot, save. Keeps `pendingSnapshot.current` updated so unmount
    // cleanup can flush an in-progress edit before the component disappears.
    useEffect(() => {
        if (!companyId) return undefined;
        if (sameTemplate(edits, baselineRef.current)) return undefined;
        if (flashTimer.current) {
            clearTimeout(flashTimer.current);
            flashTimer.current = null;
        }
        setStatus('pending');
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const snapshot = sanitizeForSave(edits);
        pendingSnapshot.current = snapshot;
        debounceTimer.current = setTimeout(() => {
            runSave(snapshot);
        }, AUTOSAVE_DEBOUNCE_MS);
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [edits, companyId, runSave]);

    // Unmount: flush any debounced-but-unfired save before this component goes away.
    // The setDoc is fire-and-forget after unmount — the Firebase SDK continues the network
    // call regardless, and we suppress any post-unmount state updates via `isMounted`.
    useEffect(() => () => {
        isMounted.current = false;
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        if (retryTimer.current) clearTimeout(retryTimer.current);
        const pending = pendingSnapshot.current;
        if (pending && companyId) {
            // Fire-and-forget. Errors are intentionally swallowed — there's no UI left to show them.
            performSave(pending).catch(() => {});
        }
    }, [companyId, performSave]);

    const setRaw = (key, value) => setRawInputs(prev => ({ ...prev, [key]: value }));
    const clearRaw = (key) => setRawInputs(prev => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
    });
    const getDisplay = (key, canonical) =>
        rawInputs[key] !== undefined ? rawInputs[key] : String(canonical);

    const updateLevelPrice = (index, raw) => {
        const key = `level_${index}_price`;
        setRaw(key, raw);
        const num = parseFloat(raw);
        if (isNaN(num)) return; // keep editing — don't commit garbage
        const clamped = Math.max(0, num);
        setEdits(prev => ({
            ...prev,
            levels: prev.levels.map((l, i) => i === index ? { ...l, pricePerSqft: clamped } : l),
        }));
    };

    const updateScalingField = (field, raw, transform) => {
        const key = `scaling_${field}`;
        setRaw(key, raw);
        const num = parseFloat(raw);
        if (isNaN(num)) return;
        let value = transform ? transform(num) : num;
        if (SCALING_FIELD_CLAMPS[field]) value = SCALING_FIELD_CLAMPS[field](value);
        setEdits(prev => ({ ...prev, [field]: value }));
    };

    const previewLevel = edits.levels.find(l => l.level === 3) || edits.levels[Math.floor(edits.levels.length / 2)] || edits.levels[0];

    const statusLabel =
        status === 'pending' ? 'Unsaved changes…' :
        status === 'saving' ? 'Saving…' :
        status === 'saved' ? 'All changes saved' :
        status === 'error' ? 'Could not save — will retry' :
        '';
    const statusClass =
        status === 'error' ? 'colour-error' :
        status === 'saved' ? 'colour-jade-03' :
        'colour-secondary';

    const perStepDiscountPct = Math.round((1 - edits.scalingBase) * 1000) / 10;
    const maxDiscountPct = Math.round((1 - edits.minScalingFactor) * 100);

    if (!isInitialized) {
        return (
            <div className="repair-template-editor">
                <Loading />
            </div>
        );
    }

    return (
        <div className="repair-template-editor">
            <div className="repair-template-status-row">
                <span className={`body-regular ${statusClass}`} aria-live="polite">{statusLabel}</span>
            </div>
            <div className="repair-template-section">
                <div className="repair-template-section-header">
                    <h3 className="heading-small-bold colour-primary">Rehab levels</h3>
                    <span className="body-regular colour-secondary">
                        Pick what you'd budget at each scope. The estimator picks the closest level for every property, then multiplies by square footage.
                    </span>
                </div>
                <div className="repair-template-levels">
                    {edits.levels.map((level, index) => {
                        const rawKey = `level_${index}_price`;
                        return (
                            <div key={level.id || `level_${index}`} className="repair-template-level-card">
                                <div className="repair-template-level-meta">
                                    <div className="repair-template-level-title-row">
                                        <span className="body-bold colour-primary">{level.name}</span>
                                        {level.timeline && (
                                            <span className="repair-template-level-timeline label-regular colour-secondary">
                                                {level.timeline}
                                            </span>
                                        )}
                                    </div>
                                    {level.description && (
                                        <span className="body-regular colour-secondary repair-template-level-description">
                                            {level.description}
                                        </span>
                                    )}
                                </div>
                                <div className="repair-template-level-price">
                                    <Input
                                        label="$/sqft"
                                        labelType="small"
                                        value={getDisplay(rawKey, level.pricePerSqft ?? '')}
                                        onChange={(e) => updateLevelPrice(index, e.target.value)}
                                        onBlur={() => clearRaw(rawKey)}
                                        placeholder="0"
                                        type="text"

                                        leftUnit="$"
                                        rightUnit={null}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={false}
                                        multiline={false}
                                        error={false}
                                        info={false}
                                        reset={false}
                                        prompt={false}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="repair-template-section">
                <div className="repair-template-section-header">
                    <h3 className="heading-small-bold colour-primary">Size scaling</h3>
                    <span className="body-regular colour-secondary">
                        Fixed-cost items (kitchen, baths, systems) don't scale with floor area, so $/sqft gets a discount on larger homes — with a cap so estimates stay realistic on very large properties.
                    </span>
                </div>
                <div className="repair-template-scaling-grid">
                    <Input
                        label="Full price up to (sqft)"
                        labelType="default"
                        info={true}
                        infoText="Homes at or below this size pay full $/sqft with no discount."
                        value={getDisplay('scaling_scalingBaselineSqft', edits.scalingBaselineSqft)}
                        onChange={(e) => updateScalingField('scalingBaselineSqft', e.target.value)}
                        onBlur={() => clearRaw('scaling_scalingBaselineSqft')}
                        placeholder="1500"
                        type="text"

                        leftUnit={null}
                        rightUnit="sqft"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        reset={false}
                        prompt={false}
                    />
                    <Input
                        label="Discount step (sqft)"
                        labelType="default"
                        info={true}
                        infoText="Each additional step above the baseline applies the discount once."
                        value={getDisplay('scaling_scalingInterval', edits.scalingInterval)}
                        onChange={(e) => updateScalingField('scalingInterval', e.target.value)}
                        onBlur={() => clearRaw('scaling_scalingInterval')}
                        placeholder="500"
                        type="text"

                        leftUnit={null}
                        rightUnit="sqft"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        reset={false}
                        prompt={false}
                    />
                    <Input
                        label="Discount per step"
                        labelType="default"
                        info={true}
                        infoText="How much cheaper each step above the baseline gets, compounding."
                        value={getDisplay('scaling_scalingBase', perStepDiscountPct)}
                        onChange={(e) => updateScalingField('scalingBase', e.target.value, (n) => Math.max(0, Math.min(1, 1 - n / 100)))}
                        onBlur={() => clearRaw('scaling_scalingBase')}
                        placeholder="5"
                        type="text"

                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        reset={false}
                        prompt={false}
                    />
                    <Input
                        label="Maximum discount"
                        labelType="default"
                        info={true}
                        infoText="Hard cap on the total discount, so very large homes don't underestimate."
                        value={getDisplay('scaling_minScalingFactor', maxDiscountPct)}
                        onChange={(e) => updateScalingField('minScalingFactor', e.target.value, (n) => Math.max(0, Math.min(1, 1 - n / 100)))}
                        onBlur={() => clearRaw('scaling_minScalingFactor')}
                        placeholder="25"
                        type="text"

                        leftUnit={null}
                        rightUnit="%"
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={false}
                        multiline={false}
                        error={false}
                        reset={false}
                        prompt={false}
                    />
                </div>
            </div>

            {previewLevel && (
                <div className="repair-template-section">
                    <div className="repair-template-section-header">
                        <h3 className="heading-small-bold colour-primary">Preview</h3>
                        <span className="body-regular colour-secondary">
                            Estimated rehab cost at <span className="body-bold colour-primary">{previewLevel.name}</span> ({formatCurrency(previewLevel.pricePerSqft)}/sqft) across common house sizes.
                        </span>
                    </div>
                    <div className="repair-template-preview-grid">
                        {PREVIEW_SQFTS.map(sqft => {
                            const cost = computeCost(previewLevel.pricePerSqft, sqft, edits);
                            const safeInterval = Math.max(1, edits.scalingInterval);
                            const steps = Math.max(0, Math.floor((sqft - edits.scalingBaselineSqft) / safeInterval));
                            const factor = Math.max(Math.pow(edits.scalingBase, steps), edits.minScalingFactor);
                            const discountPct = Math.round((1 - factor) * 100);
                            return (
                                <div key={sqft} className="repair-template-preview-cell">
                                    <span className="label-regular colour-secondary">{sqft.toLocaleString()} sqft</span>
                                    <span className="heading-small-bold colour-primary">{formatCurrency(cost)}</span>
                                    <span className="label-regular colour-secondary">
                                        {discountPct > 0 ? `${discountPct}% size discount` : 'no discount'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
    );
}

export default RepairCostsLibrary;
